import {
  currentConsentsResponseSchema,
  registerRequestSchema,
  type CurrentConsent,
  type RegisterRequest,
} from '@koderek/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Anchor, Button, Checkbox, Fieldset, Loader, PasswordInput, Stack, Text, TextInput, Typography } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';
import { z } from 'zod';
import { api, apiGet } from '../api/client';
import { AuthCard } from '../features/auth/AuthCard';
import { ResendActivation } from '../features/auth/ResendActivation';
import { errorMessage, safeReturnTo } from '../features/auth/session';
import { t } from '../texts/pl';
import classes from './RegisterPage.module.css';

// Powtórzenie hasła tylko w formularzu (literówka w haśle) – do API nie trafia.
const formSchema = registerRequestSchema
  .omit({ acceptedConsentVersionIds: true })
  .extend({ passwordRepeat: z.string() })
  .refine((v) => v.password === v.passwordRepeat, { path: ['passwordRepeat'], message: t.fields.passwordMismatch });
type FormValues = z.input<typeof formSchema>;

/** US-201: rejestracja konta ze zgodami w aktualnej wersji (US-211). */
export function RegisterPage() {
  // US-409 AC-2: rejestracja z linku wczesnego dostępu – token idzie z formularzem.
  const [params] = useSearchParams();
  const earlyAccessToken = params.get('wczesny') ?? undefined;
  // US-902 AC-2: po potwierdzeniu e‑maila powrót tam, skąd zaczęła się rejestracja (np. do zajęć).
  const back = params.get('powrot');
  const returnTo = back && safeReturnTo(back) === back ? back : undefined;
  const consents = useQuery({
    queryKey: ['consents', 'current'],
    queryFn: () => apiGet('/consents/current', currentConsentsResponseSchema),
    staleTime: 5 * 60_000,
  });
  const [accepted, setAccepted] = useState<Record<string, boolean>>({});
  const [consentError, setConsentError] = useState(false);
  const form = useForm<FormValues>({ resolver: zodResolver(formSchema) });
  const register = useMutation({ mutationFn: (body: RegisterRequest) => api('POST', '/auth/register', { body }) });

  if (register.isSuccess) {
    return (
      <AuthCard title={t.register.doneTitle}>
        <Text>{t.register.doneText(register.variables.email)}</Text>
        <ResendActivation defaultEmail={register.variables.email} />
      </AuthCard>
    );
  }

  const list: CurrentConsent[] = consents.data?.consents ?? [];
  const onSubmit = form.handleSubmit((values) => {
    // AC-3: wymagane zgody sprawdzamy przed wysłaniem (serwer sprawdza ponownie).
    const missing = list.some((c) => c.required && !accepted[c.versionId]);
    setConsentError(missing);
    if (missing) return;
    const { passwordRepeat: _repeat, ...body } = values;
    register.mutate({
      ...(body as RegisterRequest),
      acceptedConsentVersionIds: list.filter((c) => accepted[c.versionId]).map((c) => c.versionId),
      earlyAccessToken,
      returnTo,
    });
  });

  return (
    <AuthCard title={t.register.title} intro={t.register.intro}>
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="sm">
          <TextInput label={t.fields.firstName} autoComplete="given-name" required {...form.register('firstName')} error={form.formState.errors.firstName?.message} />
          <TextInput label={t.fields.lastName} autoComplete="family-name" required {...form.register('lastName')} error={form.formState.errors.lastName?.message} />
          <TextInput label={t.fields.email} type="email" autoComplete="email" required {...form.register('email')} error={form.formState.errors.email?.message} />
          <PasswordInput
            label={t.fields.password}
            description={t.fields.passwordHint}
            autoComplete="new-password"
            required
            {...form.register('password')}
            error={form.formState.errors.password?.message}
          />
          <PasswordInput
            label={t.fields.passwordRepeat}
            autoComplete="new-password"
            required
            {...form.register('passwordRepeat')}
            error={form.formState.errors.passwordRepeat?.message}
          />
          <TextInput label={t.fields.school} {...form.register('school')} error={form.formState.errors.school?.message} />
          <TextInput label={t.fields.referralSource} {...form.register('referralSource')} error={form.formState.errors.referralSource?.message} />

          <Fieldset legend={t.register.consentsTitle}>
            {consents.isPending ? <Loader size="sm" aria-label="Ładowanie zgód" /> : null}
            {consents.isError ? <Alert color="red">{errorMessage(consents.error)}</Alert> : null}
            <Stack gap="sm">
              {list.map((consent) => (
                <Checkbox
                  key={consent.versionId}
                  checked={!!accepted[consent.versionId]}
                  onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    setAccepted((prev) => ({ ...prev, [consent.versionId]: checked }));
                  }}
                  label={
                    <>
                      <Text span fw={600}>
                        {consent.title} {consent.required ? t.register.required : null}
                      </Text>
                      <Text size="sm" c="dimmed">
                        {consent.content}
                      </Text>
                    </>
                  }
                  error={consentError && consent.required && !accepted[consent.versionId] ? t.errors.consent_required : undefined}
                />
              ))}
            </Stack>
          </Fieldset>

          {register.isError ? (
            <Alert color="red" role="alert">
              {errorMessage(register.error)}
            </Alert>
          ) : null}
          <Button type="submit" loading={register.isPending} disabled={!list.length}>
            {t.register.submit}
          </Button>
          <Text size="sm">
            {t.register.haveAccount}{' '}
            <Anchor component={Link} to={returnTo ? `/logowanie?powrot=${encodeURIComponent(returnTo)}` : '/logowanie'}>
              {t.nav.login}
            </Anchor>
          </Text>
          {consents.data?.notice ? (
            // Klauzula informacyjna (PRD §10 pkt 35) – edytowana w panelu „Zgody”, HTML czyszczony w API.
            <Typography fz="xs" c="dimmed" className={classes.notice} data-testid="registration-notice">
              <div dangerouslySetInnerHTML={{ __html: consents.data.notice }} />
            </Typography>
          ) : null}
        </Stack>
      </form>
    </AuthCard>
  );
}
